<script setup lang="ts">
import { computed } from "vue";
import { multiplyDecimal, formatDecimal, decimalFromString } from "@evka/shared";

const props = withDefaults(
  defineProps<{
    label: string;
    unitPrice: string;
    quantity: number;
    disabled?: boolean;
    max?: number;
  }>(),
  { max: 50 },
);

const emit = defineEmits<{
  (e: "update:quantity", value: number): void;
}>();

const subtotal = computed(() => {
  if (props.quantity <= 0) return decimalFromString("0.00").toNumber();
  return multiplyDecimal(props.unitPrice, props.quantity).toNumber();
});

const subtotalDisplay = computed(() =>
  formatDecimal(multiplyDecimal(props.unitPrice, Math.max(0, props.quantity))),
);

const atMax = computed(() => props.quantity >= props.max);

function clampToMax(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > props.max) return props.max;
  return n;
}

function dec() {
  if (props.disabled) return;
  emit("update:quantity", Math.max(0, props.quantity - 1));
}
function inc() {
  if (props.disabled) return;
  emit("update:quantity", clampToMax(props.quantity + 1));
}
function onInput(e: Event) {
  if (props.disabled) return;
  const target = e.target as HTMLInputElement;
  const parsed = Number.parseInt(target.value, 10);
  emit("update:quantity", Number.isFinite(parsed) ? clampToMax(parsed) : 0);
}

// Expose `subtotal` to satisfy unused-var linters in tests if any.
defineExpose({ subtotal, subtotalDisplay });
</script>

<template>
  <div
    class="flex flex-col gap-2 border-b border-slate-200 py-3 sm:flex-row sm:items-center sm:justify-between"
  >
    <div class="flex flex-col">
      <span class="text-base font-medium text-slate-900">{{ label }}</span>
      <span class="text-sm text-slate-600">
        <!-- The price slot lets a free row say "kostenlos*" instead of CHF 0.00. -->
        <slot name="price">{{ $n(Number(unitPrice), "currency", "de-CH") }}</slot>
      </span>
    </div>
    <div class="flex items-center gap-3">
      <div class="inline-flex items-center rounded-md border border-slate-300">
        <button
          type="button"
          class="px-3 py-2 text-lg leading-none text-slate-700 disabled:opacity-50"
          :disabled="disabled || quantity <= 0"
          :aria-label="$t('buy.categories.decrease')"
          @click="dec"
        >
          −
        </button>
        <input
          type="number"
          inputmode="numeric"
          min="0"
          :max="max"
          class="w-14 border-x border-slate-300 bg-white py-2 text-center text-base focus:outline-none focus:ring-1 focus:ring-slate-400 disabled:bg-slate-100"
          :value="quantity"
          :disabled="disabled"
          :aria-label="$t('buy.categories.quantityLabel')"
          @input="onInput"
        />
        <button
          type="button"
          class="px-3 py-2 text-lg leading-none text-slate-700 disabled:opacity-50"
          :disabled="disabled || atMax"
          :aria-label="$t('buy.categories.increase')"
          @click="inc"
        >
          +
        </button>
      </div>
      <span class="w-24 text-right text-sm font-semibold tabular-nums text-slate-900">
        {{ $n(Number(subtotalDisplay), "currency", "de-CH") }}
      </span>
    </div>
  </div>
</template>
